import { Center, HorizBox, Pad, PadBox } from 'component/basics';
import { CTAButton } from 'component/button';
import { BaseComments, type CommentConfig, commentTable } from 'component/comment';
import { DropDownSelector } from 'component/popup';
import { ConversationScreen } from 'component/screen';
import { Heading } from 'component/text';
import { mostRecentRanker, mostUpvotedRanker } from 'feature/ranking/simple-ranking';
import { RankingSelector } from 'feature/ranking/top-tab';
import { useState } from 'react';
import { type Datastore, useCollection, useDatastore } from 'util/datastore';
import { useConfig } from 'util/features';
import { type Feature, t } from 'util/stdtypes';

export const MostPopularFeature: Feature<CommentConfig, {}> = {
    key: 'most-popular',
    name: 'Only Most Popular Comments',
    dev: true,
    subscreens: {
        allComments: AllCommentsScreen,
    },
    providesConfig: {
        pageTopWidgets: [MostPopularLabel],
        pageBottomWidgets: [ViewAllButton],
        commentRankers: [
            {
                key: 'most-popular',
                label: t('Most popular'),
                ranker: mostUpvotedRanker,
            },
            {
                key: 'most-recent',
                label: t('Most recent'),
                ranker: mostRecentRanker,
            },
        ],
        commentLimit: () => 5,
    },
};

export function MostPopularLabel() {
    return (
        <PadBox top={32}>
            <Heading label={t('Most popular')} type='small' weight='medium' />
        </PadBox>
    );
}

export function ViewAllButton() {
    const datastore = useDatastore();
    const { commentLimit } = useConfig<CommentConfig>();
    const limit = commentLimit?.(datastore);
    const topLevelComments = useCollection(commentTable, { filter: { about: null, replyTo: null, deleted: null } });
    if (limit && topLevelComments.length <= limit) return null;

    return (
        <PadBox top={32}>
            <Center>
                <CTAButton label={t('Show more responses')} onPress={() => datastore.pushSubscreen('allComments', {})} type='primary' />
            </Center>
        </PadBox>
    );
}

export function AllCommentsScreen() {
    const { commentRankers } = useConfig<CommentConfig>();
    const options = commentRankers.map((ranker) => ({ key: ranker.key, label: ranker.label }));

    const [sortMode, setSortMode] = useState(options[0].key);

    return (
        <ConversationScreen>
            <PadBox horiz={20} top={32}>
                <HorizBox spread>
                    <Heading label={t('All contributions')} type='small' weight='medium' />
                    <DropDownSelector onChange={setSortMode} options={options} testID='ranking-selector' value={sortMode} />
                </HorizBox>
                <Pad size={32} />
                <BaseComments about={null} overrideCommentLimit overrideSortMode={sortMode} />
                <Pad size={32} />
            </PadBox>
        </ConversationScreen>
    );
}
